import { z } from 'zod';
import { APARTMENT_NOTE_MAX } from './apartment-sheet.js';
import { REINSPECTION_RESULTS, handoverSummarySchema } from './handover.js';
import { APARTMENT_EXCEPTIONS, HANDOVER_STAGES } from './navigator.js';
// The apartment workspace (U2, docs/product-ux 05 screen C): one page per apartment with its
// identity, spaces, handovers, open defects, contacts and note. Each part is present only for a
// reader holding its keys; an absent part is null, never an empty placeholder that looks real.
const space = z
    .object({ number: z.string(), location: z.string(), structureCode: z.string() })
    .strict();
const appointment = z
    .object({
    startsAt: z.iso.datetime({ offset: true }),
    durationMinutes: z.number().int().min(1),
    status: z.string(),
    /** The responsible employee's name, if one is assigned. */
    primary: z.string().nullable(),
})
    .strict();
export const CONTACT_RELATIONSHIPS = [
    'source_listed',
    'purchaser',
    'additional_purchaser',
    'occupant',
    'contact_person',
];
/** One person listed for the apartment (personal data, DR-185). */
export const apartmentContactSchema = z
    .object({
    relationship: z.enum(CONTACT_RELATIONSHIPS),
    name: z.string().nullable(),
    phones: z.array(z.string()),
    emails: z.array(z.string()),
})
    .strict();
/** An open finding of one of the apartment's handovers. */
export const apartmentDefectSchema = z
    .object({
    id: z.uuid(),
    handoverId: z.uuid(),
    handoverType: z.enum(['initial', 'final']),
    protocolNumber: z.string(),
    area: z.string(),
    component: z.string(),
    description: z.string(),
    contractor: z.string().nullable(),
    inspectedOn: z.iso.date(),
    /** The latest reinspection of this finding by a final handover; null: not reinspected yet. */
    reinspection: z.enum(REINSPECTION_RESULTS).nullable(),
})
    .strict();
/** The apartment's note and the version it was read at (null: no note stored). */
export const apartmentNoteSchema = z
    .object({ text: z.string().nullable(), rowVersion: z.number().int().min(1).nullable() })
    .strict();
export const apartmentWorkspacePermissionsSchema = z
    .object({
    handoverView: z.boolean(),
    handoverCreate: z.boolean(),
    handoverEdit: z.boolean(),
    contactView: z.boolean(),
    noteView: z.boolean(),
    noteEdit: z.boolean(),
    /** The apartment's change history (`audit.project.view`). */
    history: z.boolean(),
    /** Editing the apartment in Master Data. */
    masterData: z.boolean(),
})
    .strict();
/** `GET /projects/:projectId/apartments/:unitId`. */
export const apartmentWorkspaceResponseSchema = z
    .object({
    project: z.object({ id: z.uuid(), code: z.string(), name: z.string() }).strict(),
    apartment: z
        .object({
        id: z.uuid(),
        /** The building (structure) id: the navigator's context (U3). */
        buildingId: z.uuid(),
        building: z.string(),
        /** The floor's label ('' when the apartment has no floor). */
        floor: z.string(),
        apartmentNumber: z.string(),
        /** The original number in the source, and the floor as the source states it (DR-183). */
        sourceNumber: z.string().nullable(),
        sourceFloorLabel: z.string().nullable(),
        modelType: z.string().nullable(),
        parking: z.array(space),
        storage: z.array(space),
    })
        .strict(),
    /** Null without `handover.handover.view`. Newest first per type. */
    handovers: z.array(handoverSummarySchema).nullable(),
    /** The active appointment of each handover type; null without `handover.handover.view`. */
    appointments: z
        .object({ initial: appointment.nullable(), final: appointment.nullable() })
        .strict()
        .nullable(),
    /** Open findings, oldest first; null without `handover.handover.view`. */
    defects: z.array(apartmentDefectSchema).nullable(),
    /** Null without `projects.contact.view` (DR-185). */
    contacts: z.array(apartmentContactSchema).nullable(),
    /** Null without `projects.note.view` (DR-187). */
    note: apartmentNoteSchema.nullable(),
    /** The same server-computed stage and exceptions as the navigator (08 §1–2). */
    stage: z.enum(HANDOVER_STAGES),
    exceptions: z.array(z.enum(APARTMENT_EXCEPTIONS)),
    permissions: apartmentWorkspacePermissionsSchema,
})
    .strict();
/**
 * `PUT /projects/:projectId/apartments/:unitId/note`: the whole note (null or blank erases it),
 * with the version it was edited from (null: there was no note). A stale version is refused (409).
 */
export const apartmentNoteRequestSchema = z
    .object({
    text: z
        .string()
        .max(APARTMENT_NOTE_MAX + 50)
        .nullable(),
    rowVersion: z.number().int().min(1).nullable(),
})
    .strict();
//# sourceMappingURL=apartment-workspace.js.map